import React, { Component } from 'react'
import cx from 'classnames'

import { Button, LoadingIndicator } from '@wf-common/atoms'

import styles from './with-loading-animation.module.css'

const propTypes = {
    ...Button.propTypes
}

/**
 * A HOC to add a loading animation to a button, where the underlying onClick task is currently in progress.
 *
 * To activate the blocking loading animation, the onClick methode needs to return a pending Promise.
 * Once the Promise is resolved or rejected the loading animation disappears.
 *
 * The LoadingAnimationButton HOC passed all props to the wrapped <Button />,
 * means it accept the same props than  <Button />.
 *
 * @deprecated Please use the LoadingIndicatorButton of the `@wf-common/molecules` library and handle the state inside of the using component
 *
 * @example <caption>add loading animation to a button</caption>
 * <code>
 *     // create your component
 *     const LoadingAnimationButton = withLoadingAnimation(Button)
 *
 *     // use the component
 *     <LoadingAnimationButton primary
 *          onClick={() => onClick(filteredNotifications)}>
 *          {buttonText}
 *     </LoadingAnimationButton>
 * </code>
 *
 * @param {React.Component} StyledComponent
 * @return {React.Component}
 */
export const withLoadingAnimation = WrappedComponent => {
    const Wrapper = class extends Component {
        constructor(props) {
            super(props)
            this.onClickWrapper = this.onClickWrapper.bind(this)
            this.state = {
                loadingAnimation: false
            }
        }

        render() {
            const { children, className, disabled, onClick } = this.props
            const { loadingAnimation } = this.state

            const composedClassName = loadingAnimation ? cx(className, styles['button']) : className

            return (
                <div className={styles['container']}>
                    <WrappedComponent
                        {...this.props}
                        className={composedClassName}
                        onClick={onClick && this.onClickWrapper}
                        disabled={disabled || loadingAnimation}
                    >
                        {children}
                    </WrappedComponent>
                    {loadingAnimation && (
                        <LoadingIndicator
                            className={styles['loading-indicator']}
                            classNameAnimation={styles['loading-indicator-animation']}
                            classNameAnimationCircle={styles['loading-indicator-circle']}
                            isLoading={true}
                        />
                    )}
                </div>
            )
        }

        onClickWrapper() {
            const { onClick } = this.props
            this.setState({ loadingAnimation: true })

            const possiblePromise = onClick && onClick()
            if (possiblePromise instanceof Promise) {
                possiblePromise.then(
                    () => this.setState({ loadingAnimation: false }),
                    () => this.setState({ loadingAnimation: false })
                )
            } else {
                this.setState({ loadingAnimation: false })
            }
        }
    }

    Wrapper.propTypes = propTypes
    return Wrapper
}
